import { useId, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { IconEdit } from "@/lib/icons";
import { useModalA11y } from "@/lib/useModalA11y";
import { ApiError } from "@/lib/http";

interface Props {
  label: string;
  value: string;
  help: string;
  allowEmpty?: boolean;
  onSave: (value: string) => Promise<unknown>;
}

/** A caneta fica junto do nome; o formulario sai da sidebar via portal. */
export function RenameButton({ label, value, help, allowEmpty = false, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const close = () => { if (!submitting.current) setOpen(false); };
  const modalRef = useModalA11y({ open, onClose: close });

  async function save(event: FormEvent) {
    event.preventDefault();
    if (submitting.current) return;
    const name = draft.trim();
    if (!name && !allowEmpty) { setError("Informe um nome."); return; }
    submitting.current = true;
    setSaving(true);
    setError(null);
    try {
      await onSave(name);
      setOpen(false);
    } catch (cause) {
      const body = cause instanceof ApiError ? cause.body : null;
      setError(body && typeof body === "object" && "message" in body
        ? String(body.message) : "Não foi possível salvar o nome. Tente novamente.");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  }

  return <>
    <button type="button" className="name-edit-button" title={label} aria-label={label}
      onClick={() => { setDraft(value); setError(null); setOpen(true); }}>
      <IconEdit size={14} />
    </button>
    {open ? createPortal(
      <div className="modal-backdrop name-edit-backdrop" onClick={close}>
        <div className="modal" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}
          ref={modalRef} onClick={(event) => event.stopPropagation()}>
          <div className="modal__header"><h2 id={`${id}-title`}>{label}</h2></div>
          <form className="modal__body" onSubmit={(event) => void save(event)}>
            <label htmlFor={id}>Nome</label>
            <input id={id} className="input" value={draft} maxLength={80} disabled={saving}
              aria-describedby={`${id}-help`} aria-invalid={!!error}
              onChange={(event) => setDraft(event.target.value)} />
            <p id={`${id}-help`} className="muted">{help}</p>
            {error ? <p role="alert" className="name-edit-error">{error}</p> : null}
            <div className="modal__actions">
              <button type="button" className="btn" disabled={saving} onClick={close}>Cancelar</button>
              <button type="submit" className="btn primary" disabled={saving}>
                {saving ? "Salvando..." : "Salvar nome"}
              </button>
            </div>
          </form>
        </div>
      </div>, document.body,
    ) : null}
  </>;
}
